import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import useEmblaCarousel from "embla-carousel-react";
import { ArrowRight, ArrowDown } from "lucide-react";
import { HERO_SLIDES } from "../config/siteConfig";

export const Hero = () => {
  const navigate = useNavigate();
  const [emblaRef, embla] = useEmblaCarousel({ loop: true, duration: 32 });
  const [selected, setSelected] = useState(0);

  const onSelect = useCallback(() => {
    if (embla) setSelected(embla.selectedScrollSnap());
  }, [embla]);

  useEffect(() => {
    if (!embla) return;
    embla.on("select", onSelect);
    onSelect();
    const id = setInterval(() => embla.scrollNext(), 6000);
    return () => {
      clearInterval(id);
      embla.off("select", onSelect);
    };
  }, [embla, onSelect]);

  const scrollDown = () => {
    const el = document.querySelector("#below-hero");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="home" className="relative h-[100svh] min-h-[640px] w-full overflow-hidden bg-[#111111]" data-testid="hero-section">
      <div className="embla h-full overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {HERO_SLIDES.map((s, i) => (
            <div className="relative flex-[0_0_100%] h-full min-w-0" key={i}>
              <img
                src={s.image}
                alt={`${s.label} — INRISOFT`}
                className={`absolute inset-0 w-full h-full object-cover ${selected === i ? "hero-kenburns" : ""}`}
              />
              <div className="absolute inset-0 bg-[#0b0b0b]/62" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-transparent to-[#111111]/40" />
            </div>
          ))}
        </div>
      </div>

      {/* Overlay content keyed to active slide so the reveal replays */}
      <div key={selected} className="absolute inset-0 z-10 h-full max-w-7xl mx-auto px-6 lg:px-8 flex flex-col justify-center pointer-events-none">
        <p className="hero-fade overline text-[#E03535] text-xs sm:text-sm mb-6 flex items-center gap-3" style={{ animationDelay: "0.05s" }}>
          <span className="h-px w-10 bg-[#E03535] inline-block" /> {HERO_SLIDES[selected].label}
        </p>

        <h1 data-testid="hero-headline" aria-label={HERO_SLIDES[selected].headline.join(" ")} className="font-display text-white text-5xl sm:text-6xl lg:text-7xl tracking-tight max-w-5xl">
          {HERO_SLIDES[selected].headline.map((line, i) => (
            <span key={i} className="block overflow-hidden py-[0.06em]" aria-hidden="true">
              <span className="hero-line leading-[1.12]" style={{ animationDelay: `${0.1 + i * 0.1}s` }}>
                {line}
              </span>
            </span>
          ))}
        </h1>

        <p className="hero-fade font-body text-white/75 text-base md:text-lg max-w-xl mt-7 leading-relaxed" style={{ animationDelay: "0.7s" }}>
          {HERO_SLIDES[selected].text}
        </p>

        <div className="hero-fade flex flex-col sm:flex-row gap-4 mt-9 pointer-events-auto" style={{ animationDelay: "0.85s" }}>
          <button
            onClick={() => navigate("/contact")}
            data-testid="hero-primary-cta"
            className="group inline-flex items-center justify-center gap-2 bg-[#C62828] text-white font-accent uppercase tracking-widest text-sm px-8 py-4 rounded-sm hover:bg-white hover:text-[#111111] transition-colors duration-400"
          >
            Say Hello
            <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => navigate("/about")}
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 border border-white/30 text-white font-accent uppercase tracking-widest text-sm px-8 py-4 rounded-sm hover:border-white hover:bg-white/5 transition-all duration-400"
          >
            About Us
          </button>
        </div>
      </div>

      {/* Slide dots */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2.5">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            onClick={() => embla && embla.scrollTo(i)}
            data-testid={`hero-dot-${i}`}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-400 ${selected === i ? "w-9 bg-[#E03535]" : "w-4 bg-white/40 hover:bg-white/70"}`}
          />
        ))}
      </div>

      <button
        onClick={scrollDown}
        data-testid="hero-scroll-indicator"
        aria-label="Scroll down"
        className="absolute bottom-7 right-8 z-20 text-white/70 hover:text-white transition-colors hidden md:flex flex-col items-center gap-2"
      >
        <span className="overline text-[10px]">Scroll</span>
        <ArrowDown size={18} className="hero-bounce" />
      </button>
    </section>
  );
};

export default Hero;
